import React from 'react';
import PropTypes from 'prop-types';
import OverlayController from '../overlay/OverlayController';
import _ from 'lodash';

const TEMP_CLIENT_KEY_PREFIX = "TEMP_KEY_";
const META_STATE_FILL_COLOR = "#56c8d8";
const STATE_FILL_COLOR = "#ffffff";
const STATE_STROKE_COLOR = "#333333";
const STATE_INCLUDED_FILL_COLOR = "#66BB6A";
const STATE_INCLUDED_STROKE_COLOR = "#1B5E20";
const STATE_EXCLUDED_FILL_COLOR = "#E57373";
const STATE_EXCLUDED_STROKE_COLOR = "#B71C1C";

const STATE_TYPES = {
    NULL: "null",
    FROM_ANY: "fromAny",
    NORMAL: "normal"
};

const STATE_TYPE_NAMES = {
    NULL: "null",
    FROM_ANY: "from_any"
};

export default class StateDiagram extends React.Component {

    static propTypes = {
        project: PropTypes.shape({
            aclRole: PropTypes.string.isRequired,
            states: PropTypes.array.isRequired,
            flows: PropTypes.array.isRequired
        }).isRequired,
        delegate: PropTypes.shape({
            stateDiagramDidSelectMixedObjects: PropTypes.func.isRequired,
            stateDiagramDidSelectTransition: PropTypes.func.isRequired,
            stateDiagramDidSelectStates: PropTypes.func.isRequired,
            stateDiagramDidCreateFlow: PropTypes.func.isRequired,
            stateDiagramDidUpdateFlow: PropTypes.func.isRequired,
            stateDiagramDidDeleteFlow: PropTypes.func.isRequired,
            stateDiagramDidCreateState: PropTypes.func.isRequired,
            stateDiagramDidUpdateState: PropTypes.func.isRequired,
            stateDiagramDidDeleteState: PropTypes.func.isRequired,
            stateDiagramDidCreateFromAnyState: PropTypes.func.isRequired,
            stateDiagramDidCreateNullState: PropTypes.func.isRequired,
            stateDiagramDidInsertLink: PropTypes.func.isRequired,
            stateDiagramDidClickChangeTransitionEvent: PropTypes.func.isRequired,
            stateDiagramDidDeleteTransition: PropTypes.func.isRequired,
        }).isRequired
    };

    static defaultProps = {};

    static nullStateNodeData(options) {
        let data = {
            type: options.type,
            text: options.text,
            location: options.location,
            deletable: true,
            reasons: null,
            textEditable: false,
            fillColor: META_STATE_FILL_COLOR,
            toLinkable: false
        };
        if (options.key) {
            data.key = options.key;
        }
        return data;
    }

    static fromAnyStateNodeData(options) {
        let data = {
            type: options.type,
            text: options.text,
            group: options.group,
            location: options.location,
            deletable: true,
            reasons: null,
            textEditable: false,
            fillColor: META_STATE_FILL_COLOR,
            toLinkable: false
        };
        if (options.key) {
            data.key = options.key;
        }
        return data;
    }

    static normalStateNodeData(options) {
        let data = {
            type: options.type,
            text: options.text,
            group: options.group,
            location: options.location,
            deletable: options.deletable,
            reasons: options.reasons,
            textEditable: true,
            toLinkable: true,
            selectedStateFilterType: null
        };
        if (options.key) {
            data.key = options.key;
        }
        return data;
    }

    static stringifyPoint(point) {
        return parseInt(point.x, 10) + ',' + parseInt(point.y, 10);
    }

    static pointsEqual(pointA, pointB) {
        let _pointA = {x: parseInt(pointA.x, 10), y: parseInt(pointA.y, 10)};
        let _pointB = {x: parseInt(pointB.x, 10), y: parseInt(pointB.y, 10)};
        return _pointA.x === _pointB.x && _pointA.y === _pointB.y;
    }

    constructor(props) {
        super(props);

        this.go = window.go;
        this.$ = this.go.GraphObject.make;
        this.make = this.go.GraphObject.make;
        this.tempKeyCounter = 0;
        this.diagramDivElm = null;
        this.onWindowResizeHandlerBound = this.onWindowResizeHandler.bind(this);
        this.getUniqueKey = this.getUniqueKey.bind(this);

        this.state = {
            diagramDivHeight: "auto"
        };
    }

    render() {
        return (
            <div id="diagramContainer"
                 style={{height: this.state.diagramDivHeight}}
                 ref={(elm) => this.diagramDivElm = elm}/>
        );
    }

    componentDidMount() {
        this.createDiagram();
        this.extendCanDeleteSelection();
        this.addDiagramContextMenu();

        this.setNodeTemplate();
        this.setLinkTemplate();
        this.setGroupTemplate();

        this.addObjectDoubleClickedDiagramListener();
        this.addLinkDataArchetype();
        this.addTextEditingToolTextEditorValueFunction();
        this.addSelectionChangedDiagramListener();
        this.addModelChangedListener();

        this.setDiagramModel();
        this.setDiagramHeight();

        window.addEventListener("TaskboardWindowResize", this.onWindowResizeHandlerBound, false);
    }

    componentWillUnmount() {
        window.removeEventListener("TaskboardWindowResize", this.onWindowResizeHandlerBound, false);
    }

    onWindowResizeHandler() {
        this.setDiagramHeight();
    }

    componentDidUpdate() {
        // The state of this component changes only when the setDiagramHeight() is called to update the diagramDivHeight
        // When the state changes, we need to notify the diagram so it will update itself.
        this.diagram.requestUpdate();
    }

    updateLinkData(linkData) {
        let _linkData = this.diagram.model.findLinkDataForKey(linkData.key);
        this.diagram.model.setDataProperty(_linkData, 'text', linkData.text);
        this.diagram.model.setDataProperty(_linkData, 'hasAction', linkData.hasAction);
    }

    updateLinkActions(links) {
        _.forEach(links, link => {
            let _linkData = this.diagram.model.findLinkDataForKey(link.key);
            this.diagram.model.setDataProperty(_linkData, 'hasAction', link.hasAction);
        });
    }

    updateLinkText(linkDataKey, text) {
        let linkData = this.diagram.model.findLinkDataForKey(linkDataKey);
        this.diagram.model.setDataProperty(linkData, "text", text);
    }

    clearSelection() {
        this.diagram.clearSelection();
    }

    selectTransition(transition) {
        let link = this.diagram.findPartForKey(transition.id);
        // Pre GoJS 1.7
        // let linkData = this.diagram.model.findLinkDataForKey(transition.id);
        // let link = this.diagram.findLinkForData(linkData);
        this.diagram.select(link);
    }

    clearHighlights() {
        if (this.diagram.highlighteds.size === 0) {
            return;
        }
        this.diagram.startTransaction("clearHighlights");
        this.diagram.highlighteds.toArray().forEach(node => {
            this._highlightNode(node, null);
        });
        this.diagram.commitTransaction("clearHighlights");
    }

    highlightStates(states) {
        this.clearHighlights();
        this.diagram.startTransaction("highlightStates");
        this._highlightStates(states.stateIdsIncluded, "included");
        this._highlightStates(states.stateIdsExcluded, "excluded");
        this.diagram.commitTransaction("highlightStates");
    }

    highlightSelectedStates() {
        let selection = this.diagram.selection;
        if (!this.doesSelectionContainNormalStatesOnly(selection)) {
            return;
        }
        this.diagram.startTransaction("highlightSelectedStates");
        selection.each(node => {
            this._highlightNode(node, node.data.selectedStateFilterType);
        });
        this.diagram.commitTransaction("highlightSelectedStates");
    }

    _highlightStates(stateIds, filterType) {
        stateIds.forEach(stateId => {
            let node = this.diagram.findNodeForKey(stateId);
            this._highlightNode(node, filterType);
        });
    }

    _highlightNode(node, filterType) {
        if (filterType === "included") {
            this._highlightNodeWithColors(node, STATE_INCLUDED_FILL_COLOR, STATE_INCLUDED_STROKE_COLOR);
            node.isHighlighted = true;
        } else if (filterType === "excluded") {
            this._highlightNodeWithColors(node, STATE_EXCLUDED_FILL_COLOR, STATE_EXCLUDED_STROKE_COLOR);
            node.isHighlighted = true;
        } else {
            this._highlightNodeWithColors(node, STATE_FILL_COLOR, STATE_STROKE_COLOR);
            node.isHighlighted = false;
        }
    }

    _highlightNodeWithColors(node, fillColor, strokeColor) {
        this.diagram.model.setDataProperty(node.data, "fillColor", fillColor);
        this.diagram.model.setDataProperty(node.data, "strokeColor", strokeColor);
        // let shape = node.findObject("stateRectangle");
        // shape.fill = fillColor;
        // shape.stroke = strokeColor;
    }

    // selectStates(states) {
        // this.diagram.startTransaction("selectStates");
        // this.diagram.commitTransaction("selectStates");
    // }

    setDiagramHeight() {
        let offsetParent = this.diagramDivElm;
        let offsetTop = 0;
        while (offsetParent) {
            offsetTop += offsetParent.offsetTop;
            offsetParent = offsetParent.offsetParent;
        }
        let documentHeight = document.documentElement.clientHeight;
        let height = documentHeight - offsetTop - 20;
        height += "px";
        if (this.state.diagramDivHeight !== height) {
            this.setState({diagramDivHeight: height});
        }
    }

    createDiagram() {
        let go = this.go;

        /** @type {go.Diagram} */
        this.diagram = this.make(go.Diagram, "diagramContainer", {
            isReadOnly: this.props.project.aclRole !== "admin",

            // position the graph in the middle of the diagram
            initialContentAlignment: go.Spot.Center,

            // allow double-click in background to create a new node
            "clickCreatingTool.archetypeNodeData": { text: "flow", isGroup: true },

            "textEditingTool.starting": go.TextEditingTool.DoubleClick,

            // allow Ctrl-G to call groupSelection()
            // "commandHandler.archetypeGroupData": { text: "Group", isGroup: true },

            // enable undo & redo
            "undoManager.isEnabled": true,

            // Do not animate the initial layout
            "animationManager.isInitial": false
        });
    }

    extendCanDeleteSelection() {
        let diagram = this.diagram;
        let go = this.go;
        let commandHandler = diagram.commandHandler;

        commandHandler.canDeleteSelection = function() {
            // first, call the base functionality
            let canDeleteSelection = go.CommandHandler.prototype.canDeleteSelection.call(commandHandler);

            if (canDeleteSelection) {
                let reasons = [];
                diagram.selection.each(part => {
                    if (!part.deletable) {
                        canDeleteSelection = false;
                        reasons.push(...part.data.reasons)
                    }
                });
                if (!canDeleteSelection) {
                    OverlayController.showErrorAlert(new Error("Can not delete the selected items, due to the following reasons: " + reasons.join(' ')));
                }
            }

            console.log("canDeleteSelection => ", canDeleteSelection);
            return canDeleteSelection;
        }
    }

    addDiagramContextMenu() {
        let diagram = this.diagram;
        let go = this.go;
        let make = this.make;

        diagram.contextMenu = make(go.Adornment, "Vertical",
            make("ContextMenuButton",
                make(go.TextBlock, "Add null state"),
                {
                    click: (e) => {
                        let diagram = e.diagram;
                        let point = diagram.toolManager.contextMenuTool.mouseDownPoint;
                        diagram.startTransaction('createNullState');
                        diagram.model.addNodeData(StateDiagram.nullStateNodeData({
                            type: STATE_TYPES.NULL,
                            text: STATE_TYPE_NAMES.NULL,
                            location: point
                        }));
                        diagram.commitTransaction('createNullState');
                    }
                }
            )
        );
    }

    setNodeTemplate() {
        // the node template describes how each Node should be constructed
        let diagram = this.diagram;
        let go = this.go;
        let $ = this.$;

        diagram.nodeTemplate =
            $(go.Node, "Auto",
                {
                    locationSpot: go.Spot.Center,
                    selectionChanged: (part) => {
                        if (part.data.type !== STATE_TYPES.NORMAL) {
                            return;
                        }
                        if (part.isSelected) {
                            part.data.selectedStateFilterType = part.diagram.lastInput.alt ? "excluded" : "included";
                            // this._highlightNode(part, part.data.selectedStateFilterType);
                        } else {
                            part.data.selectedStateFilterType = null;
                            // this._highlightNode(part, part.data.selectedStateFilterType);
                        }
                    }
                },
                new go.Binding("location", "location").makeTwoWay(),
                new go.Binding("deletable", "deletable"),
                new go.Binding("textEditable", "textEditable"),
                $(go.Shape, "RoundedRectangle",
                    {
                        name: "stateRectangle",
                        parameter1: 2,
                        strokeWidth: 1,
                        fill: STATE_FILL_COLOR,
                        stroke: STATE_STROKE_COLOR,
                        portId: "",
                        cursor: "pointer",
                        fromLinkable: true,
                        fromLinkableSelfNode: false,
                        fromLinkableDuplicates: true,
                        toLinkable: true,
                        toLinkableSelfNode: false,
                        toLinkableDuplicates: true
                    },
                    new go.Binding("fill", "fillColor"),
                    new go.Binding("stroke", "strokeColor"),
                    new go.Binding("toLinkable", "toLinkable")
                ),
                $(go.TextBlock,
                    {
                        margin: new go.Margin(5, 10, 4, 10),
                        font: "16px Roboto, sans-serif",
                        editable: true
                    },
                    new go.Binding("text", "text").makeTwoWay()
                )
            );
    }

    setLinkTemplate() {
        // The link shape and arrowhead have their stroke brush data bound to the "color" property
        let diagram = this.diagram;
        let go = this.go;
        let make = this.make;

        diagram.linkTemplate =
            make(go.Link,
                {
                    relinkableFrom: false,
                    relinkableTo: false,
                    curve: go.Link.Bezier,
                    fromEndSegmentLength: 20,
                    contextMenu: make(go.Adornment, "Vertical",
                        make("ContextMenuButton",
                            make(go.TextBlock, "Change event"),
                            {
                                click: (e, /** go.GraphObject */ obj) => {
                                    let link = obj.part.adornedPart;
                                    let transitionId = link.data.key;
                                    this.props.delegate.stateDiagramDidClickChangeTransitionEvent(transitionId);
                                }
                            }
                        ),
                        // make("ContextMenuButton",
                        //     make(go.TextBlock, "Add action button"),
                        //     {
                        //         click: (e, /** go.GraphObject */ obj) => {
                        //             let link = obj.part.adornedPart;
                        //             let transitionId = link.data.key;
                        //             this.props.delegate.stateDiagramDidClickAddActionButton(transitionId);
                        //         }
                        //     },
                        //     new go.Binding("visible", "actionTitle", actionTitle => actionTitle === null)
                        // ),
                        // make("ContextMenuButton",
                        //     make(go.TextBlock, "Remove action button"),
                        //     {
                        //         click: (e, obj) => {
                        //             let link = obj.part.adornedPart;
                        //             let transitionId = link.data.key;
                        //             this.props.delegate.stateDiagramDidClickRemoveActionButton(transitionId);
                        //         }
                        //     },
                        //     new go.Binding("visible", "actionTitle", actionTitle => actionTitle !== null)
                        // )
                    )
                },
                make(go.Shape, {strokeWidth: 2}),
                make(go.Shape, {toArrow: "Standard", scale: 1.5, stroke: null}),
                // make(go.TextBlock, new go.Binding("text", "text")),
                make(go.Panel, "Auto",
                    new go.Binding("visible", "text", text => text !== null),
                    make(go.Shape, "Rectangle", {fill: "white"}),
                    make(go.Panel, "Horizontal", {padding: new go.Margin(2, 3, 2, 3)},
                        make(go.TextBlock,
                            {
                                margin: new go.Margin(0, 2, 0, 0),
                                editable: false
                            },
                            new go.Binding("text", "text").makeTwoWay()
                        ),
                        make(go.Shape,
                            { width: 12, height: 12, figure: "circle", fill: 'orange', stroke: null},
                            new go.Binding("visible", "hasAction")
                        )
                    )
                )
            );
    }

    setGroupTemplate() {
        // Groups consist of a title in the color given by the group node data
        // above a translucent gray rectangle surrounding the member parts
        let diagram = this.diagram;
        let go = this.go;
        let make = this.make;

        diagram.groupTemplate =
            make(go.Group, "Vertical",
                {
                    // layout: $(go.LayeredDigraphLayout),
                    locationSpot: go.Spot.Center,
                    selectionObjectName: "PANEL",  // selection handle goes around shape, not label
                    ungroupable: true,  // enable Ctrl-Shift-G to ungroup a selected Group
                    contextMenu: make(go.Adornment, "Vertical",
                        make("ContextMenuButton",
                            make(go.TextBlock, "Add from_any state"),
                            { click: (e, obj) => {
                                let group = obj.part.adornedPart;
                                let diagram = e.diagram;
                                let point = diagram.toolManager.contextMenuTool.mouseDownPoint;
                                diagram.startTransaction('createFromAnyState');
                                diagram.model.addNodeData(StateDiagram.fromAnyStateNodeData({
                                    type: STATE_TYPES.FROM_ANY,
                                    text: STATE_TYPE_NAMES.FROM_ANY,
                                    group: group.data.key,
                                    location: point
                                }));
                                diagram.commitTransaction('createFromAnyState');
                            } }
                        )
                    )
                },
                new go.Binding("location", "location").makeTwoWay(),
                new go.Binding("deletable", "deletable"),
                make(go.TextBlock,
                    {
                        font: "bold 18px Roboto, sans-serif",
                        isMultiline: false,  // don't allow newlines in text
                        editable: true  // allow in-place editing by user
                    },
                    new go.Binding("text", "text").makeTwoWay(),
                    new go.Binding("stroke", "color")),
                make(go.Panel, "Auto",
                    {name: "PANEL"},
                    make(go.Shape, "Rectangle",  // the rectangular shape around the members
                        {
                            fill: "rgba(128,128,128,0.2)",
                            stroke: "gray",
                            strokeWidth: 1
                        }
                    ),
                    make(go.Placeholder, { padding: 10 })  // represents where the members are
                )
            );
    }

    addObjectDoubleClickedDiagramListener() {
        let diagram = this.diagram;
        let go = this.go;

        diagram.addDiagramListener("ObjectDoubleClicked", (e) => {
            let part = e.subject.part;
            if (part instanceof go.Group) {
                let point = diagram.lastInput.documentPoint;
                diagram.startTransaction("createState");
                diagram.model.addNodeData(StateDiagram.normalStateNodeData({
                    type: STATE_TYPES.NORMAL,
                    text: this.getUniqueStateName(),
                    group: part.data.key,
                    location: point,
                    deletable: true,
                    reasons: null
                }));
                diagram.commitTransaction("createState");
            }
        });
    }

    addLinkDataArchetype() {
        this.diagram.toolManager.linkingTool.archetypeLinkData = {
            text: null,
            hasAction: false
        };
    }

    addTextEditingToolTextEditorValueFunction() {
        this.diagram.toolManager.textEditingTool.textValidation = function(textblock, oldstr, newstr) {
            return newstr.length > 0;
        };
        this.diagram.toolManager.textEditingTool.defaultTextEditor.valueFunction = function() {
            let value = this.mainElement.value.toLowerCase();
            value = value.replace(/[^a-z0-9]+/g, '_');
            return value;
        };
    }

    addSelectionChangedDiagramListener() {
        let diagram = this.diagram;

        diagram.addDiagramListener("ChangedSelection", (diagramEvent) => {
            let selection = diagramEvent.diagram.selection;
            if (this.doesSelectionContainSingleLink(selection)) {
                let link = selection.first();
                let transitionId = link.data.key;
                if (transitionId.startsWith(TEMP_CLIENT_KEY_PREFIX)) {
                    return;
                }
                this.props.delegate.stateDiagramDidSelectTransition(transitionId);
            } else if (this.doesSelectionContainNormalStatesOnly(selection)) {
                let states = selection.toArray().map(node => ({
                    id: node.data.key,
                    name: node.data.text,
                    flowId: node.data.group,
                    type: node.data.type,
                    filterType: node.data.selectedStateFilterType
                }));
                this.props.delegate.stateDiagramDidSelectStates(states);
            } else {
                this.props.delegate.stateDiagramDidSelectMixedObjects();
            }
        });
    }

    doesSelectionContainSingleLink(selection) {
        return selection.size === 1 && selection.first() instanceof this.go.Link;
    }

    doesSelectionContainNormalStatesOnly(selection) {
        return selection.size > 0 && selection.all(part => part instanceof this.go.Node && part.data.type === STATE_TYPES.NORMAL);
    }

    addModelChangedListener() {
        let diagram = this.diagram;
        let go = this.go;

        diagram.addModelChangedListener(/** go.ChangedEvent */ evt => {
            // ignore unimportant Transaction events
            if (!evt.isTransactionFinished) return;
            let txn = evt.object;  // a Transaction
            if (txn === null) return;
            // iterate over all of the actual ChangedEvents of the Transaction
            let changedNodeDataMap = {};
            txn.changes.each(/** go.ChangedEvent */ e => {
                // record node insertions and removals
                if (e.modelChange === "nodeDataArray") {
                    if (e.change === go.ChangedEvent.Insert) {
                        let nodeData = e.newValue;
                        this.onNodeInsert(nodeData);
                    } else if (e.change === go.ChangedEvent.Remove) {
                        let nodeData = e.oldValue;
                        this.onNodeDelete(nodeData);
                    }
                } else if (e.modelChange === "linkDataArray") {
                    if (e.change === go.ChangedEvent.Insert) {
                        let linkData = e.newValue;
                        this.onLinkInsert(linkData);
                    } else if (e.change === go.ChangedEvent.Remove) {
                        let linkData = e.oldValue;
                        this.onLinkDelete(linkData);
                    }
                } else if (e.change === go.ChangedEvent.Property && e.model !== null && e.model !== e.object) {
                    // e.model !== e.object => ignore changes to the model (GraphLinksModel) itself (e.g.: like the 'linkKeyProperty' property)
                    let nodeData = e.object;
                    let propertyName = e.propertyName;
                    let oldValue = e.oldValue;
                    let newValue = e.newValue;
                    this.onNodeChange(nodeData, propertyName, oldValue, newValue, changedNodeDataMap);
                }
            });
            Object.keys(changedNodeDataMap).forEach(nodeKey => {
                let nodeData = changedNodeDataMap[nodeKey];
                if (nodeData.isGroup) {
                    this.props.delegate.stateDiagramDidUpdateFlow({
                        flowId: nodeData.key,
                        flowName: nodeData.text,
                        flowLocation: StateDiagram.stringifyPoint(nodeData.location)
                    });
                } else {
                    this.props.delegate.stateDiagramDidUpdateState({
                        stateId: nodeData.key,
                        stateName: nodeData.text,
                        stateLocation: StateDiagram.stringifyPoint(nodeData.location),
                    });
                }
            });
        });
    }

    setDiagramModel() {
        let diagram = this.diagram;
        let go = this.go;
        let nodes = [];
        let statesGroupedByFlowId = {};

        this.props.project.flows.forEach(flow => {
            statesGroupedByFlowId[flow.id] = [];
        });

        this.props.project.states.forEach(state => {
            if (state.type === STATE_TYPES.NORMAL || state.type === STATE_TYPES.FROM_ANY) {
                statesGroupedByFlowId[state.flowId].push(state);
            } else if (state.type === STATE_TYPES.NULL) {
                // Add null states
                nodes.push(StateDiagram.nullStateNodeData({
                    key: state.id,
                    type: state.type,
                    text: state.name,
                    location: this.parsePoint(state.location)
                }));
            }
        });

        this.props.project.flows.forEach(flow => {

            // Add group node for the flow
            let reasons = [];
            if (!flow.deletePermission.canDelete) {
                reasons.push(`Flow ${flow.name} can not be deleted: ` + flow.deletePermission.reasons.join('; ') + '.');
            }

            let flowNodeData = {
                key: flow.id,
                text: flow.name,
                location: this.parsePoint(flow.location),
                deletable: flow.deletePermission.canDelete,
                reasons: reasons,
                isGroup: true
            };
            nodes.push(flowNodeData);

            // Add nodes for each state in the flow
            statesGroupedByFlowId[flow.id].forEach(state => {
                let reasons = [];
                if (!state.deletePermission.canDelete) {
                    reasons.push(`State ${state.name} can not be deleted: ` + state.deletePermission.reasons.join('; ') + '.');
                }

                let stateNodeData;
                if (state.type === STATE_TYPES.NORMAL) {
                    stateNodeData = StateDiagram.normalStateNodeData({
                        key: state.id,
                        type: state.type,
                        text: state.name,
                        group: flow.id,
                        location: this.parsePoint(state.location),
                        deletable: state.deletePermission.canDelete,
                        reasons: reasons
                    });
                } else if (state.type === STATE_TYPES.FROM_ANY) {
                    stateNodeData = StateDiagram.fromAnyStateNodeData({
                        key: state.id,
                        type: state.type,
                        text: state.name,
                        group: flow.id,
                        location: this.parsePoint(state.location),
                    });
                }
                nodes.push(stateNodeData);

                // If one of the states non deletable, set the flow as non deletable as well.
                if (!stateNodeData.deletable) {
                    flowNodeData.deletable = false;
                    flowNodeData.reasons.push(...stateNodeData.reasons);
                }
            });

        });

        let links = this.props.delegate.linkDataForStateDiagram();

        diagram.model = new go.GraphLinksModel(nodes, links);
        diagram.model.linkKeyProperty = 'key';
        diagram.model.makeUniqueKeyFunction = this.getUniqueKey;
        diagram.model.makeUniqueLinkKeyFunction = this.getUniqueKey;
    }

    onNodeInsert(nodeData) {
        console.log("Node inserted:", nodeData);
        let diagram = this.diagram;
        let location = StateDiagram.stringifyPoint(nodeData.location);
        if (nodeData.isGroup) {
            this.props.delegate.stateDiagramDidCreateFlow({
                flowName: nodeData.text,
                flowLocation: location
            }, (flow) => {
                diagram.model.setKeyForNodeData(nodeData, flow.id);
            });
        } else if (nodeData.type === STATE_TYPES.NORMAL) {
            this.props.delegate.stateDiagramDidCreateState({
                stateName: nodeData.text,
                stateLocation: location,
                flowId: nodeData.group
            }, (state) => {
                diagram.model.setKeyForNodeData(nodeData, state.id);
            });
        } else if (nodeData.type === STATE_TYPES.FROM_ANY) {
            this.props.delegate.stateDiagramDidCreateFromAnyState({
                stateLocation: location,
                flowId: nodeData.group
            }, (state) => {
                diagram.model.setKeyForNodeData(nodeData, state.id);
            });
        } else if (nodeData.type === STATE_TYPES.NULL) {
            this.props.delegate.stateDiagramDidCreateNullState({
                stateLocation: location
            }, (state) => {
                diagram.model.setKeyForNodeData(nodeData, state.id);
            });
        }
    }

    onNodeDelete(nodeData) {
        console.log("Node removed:", nodeData);
        if (nodeData.key.startsWith(TEMP_CLIENT_KEY_PREFIX)) {
            return;
        }
        if (nodeData.isGroup) {
            this.props.delegate.stateDiagramDidDeleteFlow({flowId: nodeData.key});
        } else {
            this.props.delegate.stateDiagramDidDeleteState({stateId: nodeData.key});
        }
    }

    onLinkInsert(linkData) {
        console.log("Link inserted:", linkData);
        let data = {
            stateFromId: linkData.from,
            stateToId: linkData.to
        };
        this.props.delegate.stateDiagramDidInsertLink(data, newLinkData => {
            let diagram = this.diagram;
            if (newLinkData) {
                diagram.model.setKeyForLinkData(linkData, newLinkData.key);
                diagram.model.setDataProperty(linkData, 'text', newLinkData.text);
                diagram.model.setDataProperty(linkData, 'hasAction', newLinkData.hasAction);
            } else {
                diagram.model.removeLinkData(linkData);
            }
        });
    }

    onLinkDelete(linkData) {
        console.log("Link removed:", linkData);
        if (linkData.key.startsWith(TEMP_CLIENT_KEY_PREFIX)) {
            return;
        }
        let transitionId = linkData.key;
        this.props.delegate.stateDiagramDidDeleteTransition(transitionId);
    }

    onNodeChange(nodeData, propertyName, oldValue, newValue, changedNodeDataMap) {
        if (nodeData.key.startsWith(TEMP_CLIENT_KEY_PREFIX)) {
            return;
        }
        let diagram = this.diagram;
        if (propertyName === "text") {
            let node = diagram.findNodeForData(nodeData);
            if (node && (nodeData.isGroup || nodeData.group)) {
                console.log("Property '" + propertyName + "' of node changed from: '" + oldValue + "' to '" + newValue + "' of node:", nodeData);
                if (!(nodeData.key in changedNodeDataMap)) {
                    changedNodeDataMap[nodeData.key] = nodeData;
                }
            }
        } else if (propertyName === "location") {
            if (!(nodeData.key in changedNodeDataMap) && !StateDiagram.pointsEqual(newValue, nodeData.location)) {
                console.log("Property '" + propertyName + "' of part changed from: '" + nodeData.location + "' to '" + newValue + "'");
                changedNodeDataMap[nodeData.key] = nodeData;
            }
        }
    }

    getUniqueStateName() {
        let baseName = "state";
        let name = baseName;

        if (!this.hasStateWithName(name)) {
            return name;
        }

        for (let i = 1 ;; i++) {
            name = baseName + i;
            if (!this.hasStateWithName(name)) {
                return name;
            }
        }
    }

    hasStateWithName(name) {
        let nodeDataArray = this.diagram.model.nodeDataArray;
        for (let i = 0; i < nodeDataArray.length; i++) {
            if (nodeDataArray[i].text === name) {
                return true;
            }
        }
        return false;
    }

    parsePoint(value) {
        if (!value) {
            return null;
        }
        let pointArr = value.split(',');
        return new this.go.Point(parseFloat(pointArr[0]), parseFloat(pointArr[1]));
    }

    getUniqueKey() {
        return TEMP_CLIENT_KEY_PREFIX + (++this.tempKeyCounter);
    }

}



// WEBPACK FOOTER //
// ./src/components/diagram/StateDiagram.js